import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiBell,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronLeft,
  FiClock,
  FiCreditCard,
  FiDownload,
  FiEdit3,
  FiGlobe,
  FiGrid,
  FiInfo,
  FiLock,
  FiLogOut,
  FiMail,
  FiMapPin,
  FiPackage,
  FiPhone,
  FiSave,
  FiSettings,
  FiShield,
  FiShoppingBag,
  FiShoppingCart,
  FiUploadCloud,
  FiUser,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { api } from '../services/api';
import { resolveSettingMediaUrl, useSiteSettings } from '../context/SiteSettingsContext';
import UserServiceOrders from '../components/UserServiceOrders';
import UserPayments from '../components/UserPayments';
import { paymentStatusLabel, statusInfo } from '../utils/serviceOrders';
import {
  parseUserPanelPath,
  userServiceOrderDetailPath,
  userServiceOrdersPath,
} from '../utils/userRoutes';
import {
  canDownloadProductOrder,
  getCombinedDashboardSummary,
  getRecentCombinedOrders,
  getSupportSubscriptionCards,
  isPaidProductOrder,
  isPaidServiceOrder,
} from '../utils/userDashboard';
import './UserPanel.css';

const PROFILE_STORAGE_KEY = 'userPanelProfiles';

const defaultProfile = () => ({
  firstName: '',
  lastName: '',
  phone: '',
  address: '',
  city: '',
  province: '',
  website: '',
  bio: '',
  avatarUrl: '',
});

const SECTIONS = [
  {
    id: 'dashboard',
    translationKey: 'dashboard',
    icon: FiGrid,
  },
  {
    id: 'orders',
    translationKey: 'orders',
    icon: FiShoppingBag,
  },
  {
    id: 'service-orders',
    translationKey: 'serviceOrders',
    icon: FiBriefcase,
  },
  {
    id: 'payments',
    translationKey: 'payments',
    icon: FiCreditCard,
  },
  {
    id: 'settings',
    translationKey: 'settings',
    icon: FiSettings,
  },
  {
    id: 'messages',
    translationKey: 'messages',
    icon: FiMail,
  },
];

const shortOrderId = (order) => {
  if (order?.orderNumber) return order.orderNumber;
  const value = String(order?._id || order?.id || '');
  return value ? value.slice(-6).toUpperCase() : '—';
};

const SUPPORT_STATUS_TONES = {
  active: 'is-active',
  inactive: 'is-pending',
  suspended: 'is-warning',
  expired: 'is-expired',
  cancelled: 'is-expired',
};

const PRODUCT_ORDER_LABELS = {
  fa: {
    'در انتظار پرداخت': 'در انتظار پرداخت', 'آماده دانلود': 'آماده دانلود', 'لغو شده': 'لغو شده', 'بازپرداخت شده': 'بازپرداخت شده',
    'پرداخت نشده': 'پرداخت نشده', 'بخشی پرداخت شده': 'بخشی پرداخت شده', 'پرداخت شده': 'پرداخت شده', 'بازگشت وجه': 'بازگشت وجه',
  },
  ckb: {
    'در انتظار پرداخت': 'چاوەڕوانی پارەدان', 'آماده دانلود': 'ئامادەی داگرتن', 'لغو شده': 'هەڵوەشاوە', 'بازپرداخت شده': 'پارە گەڕێندراوەتەوە',
    'پرداخت نشده': 'پارە نەدراوە', 'بخشی پرداخت شده': 'بەشێک پارەدراوە', 'پرداخت شده': 'پارەدراوە', 'بازگشت وجه': 'پارە گەڕێندراوەتەوە',
  },
  en: {
    'در انتظار پرداخت': 'Awaiting payment', 'آماده دانلود': 'Ready to download', 'لغو شده': 'Cancelled', 'بازپرداخت شده': 'Refunded',
    'پرداخت نشده': 'Unpaid', 'بخشی پرداخت شده': 'Partially paid', 'پرداخت شده': 'Paid', 'بازگشت وجه': 'Refunded',
  },
};

const UserPanel = () => {
  const { t } = useTranslation();
  const { locale, localePath, formatDate: localeFormatDate, formatNumber } = useLocale();
  const { user, logout, updateCurrentUser } = useAuth();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userRoute = parseUserPanelPath(location.pathname);
  const requestedSection = searchParams.get('section');
  const initialSection = userRoute.sectionId === 'service-orders'
    ? 'service-orders'
    : SECTIONS.some((section) => section.id === requestedSection)
    ? requestedSection
    : 'dashboard';

  const [userAvatar, setUserAvatar] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [profile, setProfile] = useState(defaultProfile());
  const [profileSaveStatus, setProfileSaveStatus] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [avatarUploadError, setAvatarUploadError] = useState('');
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [downloadingOrderId, setDownloadingOrderId] = useState(null);
  const [payingOrderId, setPayingOrderId] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [serviceOrderCount, setServiceOrderCount] = useState(0);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [serviceOrdersLoading, setServiceOrdersLoading] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (user?.avatar) setUserAvatar(user.avatar);
  }, [user]);

  useEffect(() => () => {
    if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
  }, [avatarPreviewUrl]);

  useEffect(() => {
    if (userRoute.sectionId === 'service-orders') {
      setActiveSection('service-orders');
      setMobileNavOpen(false);
      return;
    }

    const section = searchParams.get('section');
    setActiveSection(SECTIONS.some((item) => item.id === section) ? section : 'dashboard');
    setMobileNavOpen(false);
  }, [searchParams, userRoute.sectionId]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const closeMobileNav = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };

    document.addEventListener('keydown', closeMobileNav);
    return () => document.removeEventListener('keydown', closeMobileNav);
  }, [mobileNavOpen]);

  useEffect(() => {
    const legacyOrderId = searchParams.get('order');
    if (
      location.pathname === localePath('/user-panel')
      && searchParams.get('section') === 'service-orders'
      && legacyOrderId
    ) {
      navigate(localePath(userServiceOrderDetailPath(legacyOrderId)), { replace: true });
    }
  }, [localePath, location.pathname, navigate, searchParams]);

  useEffect(() => {
    if (!user) return;
    let isMounted = true;
    setOrdersLoading(true);

    api.get('/api/orders/my-orders')
      .then((res) => {
        if (!isMounted) return;
        setMyOrders(res.success && Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (isMounted) setMyOrders([]);
      })
      .finally(() => {
        if (isMounted) setOrdersLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    setServiceOrdersLoading(true);
    api.get('/api/service-orders/my')
      .then((response) => {
        if (!mounted) return;
        const list = Array.isArray(response.data) ? response.data : [];
        setServiceOrders(list);
        setServiceOrderCount(list.length);
      })
      .catch(() => {
        if (!mounted) return;
        setServiceOrders([]);
        setServiceOrderCount(0);
      })
      .finally(() => {
        if (mounted) setServiceOrdersLoading(false);
      });
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    if (!user?.email) return;
    try {
      const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      const saved = all[user.email];
      if (saved && typeof saved === 'object') {
        setProfile({ ...defaultProfile(), phone: user.phone || '', ...saved });
      } else {
        setProfile({ ...defaultProfile(), phone: user.phone || '' });
      }
    } catch (_) {
      setProfile(defaultProfile());
    }
  }, [user]);

  const translatedSections = useMemo(() => SECTIONS.map((section) => ({
    ...section,
    label: t(`customerUi.userPanel.sections.${section.translationKey}.0`),
    description: t(`customerUi.userPanel.sections.${section.translationKey}.1`),
  })), [t]);
  const activeSectionInfo = translatedSections.find((section) => section.id === activeSection) || translatedSections[0];
  const ActiveSectionIcon = activeSectionInfo.icon;
  const displayName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || user?.name || t('customerUi.userPanel.user');
  const avatarValue = avatarPreviewUrl || profile.avatarUrl || userAvatar;
  const avatarSrc = avatarValue && !avatarError ? resolveSettingMediaUrl(avatarValue) : null;

  const orderSummary = useMemo(() => {
    const paidOrders = myOrders.filter(isPaidProductOrder);
    const downloadableOrders = myOrders.filter(canDownloadProductOrder);
    const totalSpent = paidOrders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0);
    return {
      total: myOrders.length,
      paid: paidOrders.length,
      downloadable: downloadableOrders.length,
      totalSpent,
    };
  }, [myOrders]);

  const dashboardSummary = useMemo(
    () => getCombinedDashboardSummary(myOrders, serviceOrders),
    [myOrders, serviceOrders]
  );
  const recentOrders = useMemo(
    () => getRecentCombinedOrders(myOrders, serviceOrders, 3),
    [myOrders, serviceOrders]
  );
  const supportSubscriptions = useMemo(
    () => getSupportSubscriptionCards(serviceOrders),
    [serviceOrders]
  );
  const dashboardLoading = ordersLoading || serviceOrdersLoading;

  const profileCompletion = useMemo(() => {
    const fields = [displayName, user?.email, profile.phone, profile.city, profile.avatarUrl || userAvatar];
    return Math.round((fields.filter(Boolean).length / fields.length) * 100);
  }, [displayName, profile.avatarUrl, profile.city, profile.phone, user?.email, userAvatar]);

  const formatAmount = (amount) => {
    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? formatNumber(numericAmount) : '—';
  };
  const formatDate = (value) => {
    if (!value || Number.isNaN(new Date(value).getTime())) return t('customerUi.userPanel.unknownDate');
    return localeFormatDate(value, { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const todayLabel = useMemo(() => (
    localeFormatDate(new Date(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  ), [localeFormatDate]);

  const changeSection = (sectionId) => {
    setMobileNavOpen(false);
    setActiveSection(sectionId);
    setDownloadError('');
    setPaymentError('');

    if (sectionId === 'service-orders') {
      navigate(localePath(userServiceOrdersPath()), { replace: Boolean(userRoute.serviceOrderId) });
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('order');
    if (sectionId === 'dashboard') nextParams.delete('section');
    else nextParams.set('section', sectionId);
    navigate({
      pathname: localePath('/user-panel'),
      search: nextParams.toString() ? `?${nextParams.toString()}` : '',
    }, { replace: true });
  };

  const saveProfile = async () => {
    if (!user?.email) return;
    setProfileSaving(true);
    setAvatarUploadError('');
    try {
      let avatar = profile.avatarUrl || user.avatar || '';
      if (avatarFile) {
        const avatarBody = new FormData();
        avatarBody.append('avatar', avatarFile);
        const avatarResponse = await api.upload('/api/auth/me/avatar', avatarBody);
        avatar = avatarResponse.data?.user?.avatar || avatar;
        setUserAvatar(avatar);
      }

      const nextProfile = { ...profile, avatarUrl: avatar };
      const response = await api.put('/api/auth/me', {
        name: [profile.firstName, profile.lastName].filter(Boolean).join(' ') || user.name,
        phone: profile.phone,
      });
      const nextUser = response.success && response.data?.user
        ? { ...response.data.user, avatar }
        : { ...user, avatar };
      const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[user.email] = nextProfile;
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(all));
      setProfile(nextProfile);
      updateCurrentUser(nextUser);
      setAvatarFile(null);
      setAvatarPreviewUrl('');
      setProfileSaveStatus('success');
      window.setTimeout(() => setProfileSaveStatus(null), 3000);
    } catch (error) {
      setAvatarUploadError(error.data?.message || error.message || '');
      setProfileSaveStatus('error');
      window.setTimeout(() => setProfileSaveStatus(null), 3000);
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile((previousProfile) => ({ ...previousProfile, [field]: value }));
    setProfileSaveStatus(null);
    if (field === 'avatarUrl') setAvatarError(false);
  };

  const handleAvatarFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    if (!allowedTypes.includes(file.type)) {
      setAvatarUploadError(t('customerUi.userPanel.invalidAvatar'));
      return;
    }
    if (file.size > 500 * 1024) {
      setAvatarUploadError(t('customerUi.userPanel.avatarTooLarge'));
      return;
    }

    setAvatarFile(file);
    setAvatarPreviewUrl(URL.createObjectURL(file));
    setAvatarUploadError('');
    setAvatarError(false);
    setProfileSaveStatus(null);
  };

  const handleLogout = () => {
    logout();
    navigate(localePath('/'));
  };

  const handleProductDownload = async (order) => {
    const orderId = order._id || order.id;
    const productId = order.product?._id || order.product?.id;
    if (!productId) return;

    setDownloadingOrderId(orderId);
    setDownloadError('');
    try {
      const { blob, filename } = await api.download(`/api/products/${productId}/download`);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename || order.product?.downloadFile?.originalName || 'product.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      setDownloadError(err.data?.message || err.message || t('customerUi.userPanel.downloadError'));
    } finally {
      setDownloadingOrderId(null);
    }
  };

  const handleProductPayment = async (order) => {
    const orderId = order._id || order.id;
    if (!orderId) return;

    setPayingOrderId(orderId);
    setPaymentError('');
    try {
      const response = await api.post(`/api/orders/${orderId}/payment-request`, {});
      if (!response.data?.paymentUrl) {
        throw new Error(t('customerUi.userPanel.gatewayMissing'));
      }
      window.location.assign(response.data.paymentUrl);
    } catch (error) {
      setPaymentError(error.data?.message || error.message || t('customerUi.userPanel.gatewayError'));
      setPayingOrderId(null);
    }
  };

  const renderOrderCard = (order) => {
    const orderId = order._id || order.id;
    const isPaid = isPaidProductOrder(order);
    const canDownload = canDownloadProductOrder(order);
    const canPay = (
      !isPaid
      && !['لغو شده', 'بازپرداخت شده'].includes(order.status)
      && Number(order.remainingAmount ?? order.amount) > 0
      && order.paymentEnabled !== false
      && order.paymentProvider === 'sepehr'
      && order.currencyCode === 'IRT'
    );
    const orderStatusTone = order.status === 'آماده دانلود'
      ? 'is-paid'
      : ['لغو شده', 'بازپرداخت شده'].includes(order.status)
        ? 'is-cancelled'
        : 'is-processing';
    return (
      <li key={orderId} className="user-panel-order-card">
        <div className="user-panel-order-product">
          <span className="user-panel-order-product-icon" aria-hidden="true"><FiPackage /></span>
          <div>
            <small>{t('customerUi.userPanel.order', { id: shortOrderId(order) })}</small>
            <strong>{order.productSnapshot?.name || order.product?.name || t('customerUi.userPanel.product')}</strong>
            <span>{formatDate(order.createdAt)}</span>
          </div>
        </div>
        <div className="user-panel-order-details">
          <div>
            <small>{t('customerUi.userPanel.orderAmount')}</small>
            <strong>{formatAmount(order.amount)} <span>{order.currencyLabel || t('customerUi.common.toman')}</span></strong>
          </div>
          <div>
            <small>{t('customerUi.userPanel.orderStatus')}</small>
            <span className={`user-panel-status-badge ${orderStatusTone}`}>
              {PRODUCT_ORDER_LABELS[locale.code]?.[order.status] || order.status || t('customerUi.userPanel.awaitingPayment')}
            </span>
          </div>
          <div>
            <small>{t('customerUi.userPanel.paymentStatus')}</small>
            <span className={`user-panel-status-badge ${isPaid ? 'is-paid' : 'is-unpaid'}`}>
              {isPaid ? <FiCheckCircle /> : <FiClock />}
              {PRODUCT_ORDER_LABELS[locale.code]?.[order.paymentStatus] || order.paymentStatus || t('customerUi.userPanel.unpaid')}
            </span>
          </div>
        </div>
        <div className="user-panel-order-download">
          {canDownload ? (
            <>
              <button
                type="button"
                onClick={() => handleProductDownload(order)}
                disabled={downloadingOrderId === orderId}
              >
                <FiDownload />
                {downloadingOrderId === orderId ? t('customerUi.userPanel.preparing') : t('customerUi.userPanel.download')}
              </button>
              <small title={order.product.downloadFile.originalName}>{order.product.downloadFile.originalName}</small>
            </>
          ) : canPay ? (
            <>
              <button
                type="button"
                className="is-payment"
                onClick={() => handleProductPayment(order)}
                disabled={payingOrderId === orderId}
              >
                <FiCreditCard />
                {payingOrderId === orderId
                  ? t('customerUi.userPanel.connecting')
                  : Number(order.paidAmount) > 0
                    ? t('customerUi.userPanel.payBalance')
                    : t('customerUi.userPanel.pay')}
              </button>
              <small>{t('customerUi.userPanel.downloadAfterPayment')}</small>
            </>
          ) : (
            <div className="user-panel-download-locked">
              <FiLock />
              <span>
                {!isPaid
                  ? t('customerUi.userPanel.cancelledPayment')
                  : t('customerUi.userPanel.fileMissing')}
              </span>
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="user-panel">
      <div className="user-panel-workspace">
        <div className="user-panel-layout">
          <aside className="user-panel-aside">
            <div className={`user-panel-aside-inner ${mobileNavOpen ? 'is-mobile-nav-open' : ''}`}>
              <div className="user-panel-brand">
                <span className="user-panel-brand-mark" aria-hidden="true"><FiUser /></span>
                <div>
                  <small>{t('customerUi.userPanel.account')}</small>
                  <strong>{settings.general.siteTitle}</strong>
                </div>
              </div>

              <div className="user-panel-user">
                <div className="user-panel-avatar">
                  {avatarSrc ? (
                    <img src={avatarSrc} alt={displayName} onError={() => setAvatarError(true)} />
                  ) : (
                    <span className="user-panel-avatar-placeholder">{displayName.charAt(0)}</span>
                  )}
                  <span className="user-panel-avatar-status" aria-label={t('customerUi.userPanel.activeAccount')} />
                </div>
                <div className="user-panel-user-info">
                  <p className="user-panel-name">{displayName}</p>
                  <p className="user-panel-email">{user?.email || ''}</p>
                </div>
              </div>

              <div className="user-panel-mobile-hub">
                <div className="user-panel-mobile-account">
                  <div className="user-panel-avatar user-panel-mobile-avatar">
                    {avatarSrc ? (
                      <img src={avatarSrc} alt="" onError={() => setAvatarError(true)} />
                    ) : (
                      <span className="user-panel-avatar-placeholder">{displayName.charAt(0)}</span>
                    )}
                    <span className="user-panel-avatar-status" aria-hidden="true" />
                  </div>
                  <div>
                    <small>{t('customerUi.userPanel.welcome')}</small>
                    <strong>{displayName}</strong>
                  </div>
                </div>

                <button
                  type="button"
                  className="user-panel-mobile-menu-trigger"
                  onClick={() => setMobileNavOpen((isOpen) => !isOpen)}
                  aria-expanded={mobileNavOpen}
                  aria-controls="user-panel-navigation"
                  aria-label={mobileNavOpen ? t('customerUi.userPanel.closeMenu') : t('customerUi.userPanel.openMenu')}
                >
                  <span className="user-panel-mobile-active-icon" aria-hidden="true">
                    <ActiveSectionIcon />
                  </span>
                  <span className="user-panel-mobile-active-copy">
                    <small>{t('customerUi.userPanel.activeSection')}</small>
                    <strong>{activeSectionInfo.label}</strong>
                  </span>
                  <span className="user-panel-mobile-chevron" aria-hidden="true">
                    <FiChevronDown />
                  </span>
                </button>
              </div>

              <nav
                id="user-panel-navigation"
                className={`user-panel-nav ${mobileNavOpen ? 'is-mobile-open' : ''}`}
                aria-label={t('customerUi.userPanel.navigation')}
              >
                {translatedSections.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`user-panel-nav-item ${activeSection === id ? 'active' : ''}`}
                    onClick={() => changeSection(id)}
                    aria-current={activeSection === id ? 'page' : undefined}
                  >
                    <span className="user-panel-nav-icon" aria-hidden="true"><Icon /></span>
                    <span className="user-panel-nav-label">{label}</span>
                    {id === 'orders' && (
                      <span className="user-panel-nav-count">{formatNumber(orderSummary.total)}</span>
                    )}
                    {id === 'service-orders' && serviceOrderCount > 0 && (
                      <span className="user-panel-nav-count">{formatNumber(serviceOrderCount)}</span>
                    )}
                  </button>
                ))}
              </nav>

              <button type="button" className="user-panel-logout" onClick={handleLogout}>
                <span aria-hidden="true"><FiLogOut /></span>
                <span>{t('customerUi.userPanel.logout')}</span>
              </button>
            </div>
          </aside>

          <main className="user-panel-main">
            {!userRoute.serviceOrderId && <header className="user-panel-topbar">
              <div className="user-panel-page-heading">
                <span className="user-panel-page-icon" aria-hidden="true"><ActiveSectionIcon /></span>
                <div>
                  <span className="user-panel-breadcrumb">{t('customerUi.userPanel.panel')} <FiChevronLeft /> {activeSectionInfo.label}</span>
                  <h1>{activeSectionInfo.label}</h1>
                  <p>{activeSectionInfo.description}</p>
                </div>
              </div>
              <div className="user-panel-today-card">
                <span aria-hidden="true"><FiCalendar /></span>
                <div>
                  <small>{t('customerUi.userPanel.today')}</small>
                  <strong>{todayLabel}</strong>
                </div>
              </div>
            </header>}

            {activeSection === 'dashboard' && (
              <div key="dashboard" className="user-panel-content user-panel-dashboard">
                <section className="user-panel-welcome-card">
                  <div>
                    <span className="user-panel-welcome-eyebrow"><FiBell /> {t('customerUi.userPanel.ready')}</span>
                    <h2>{t('customerUi.userPanel.hello', { name: displayName })}</h2>
                    <p>{t('customerUi.userPanel.welcomeHint')}</p>
                  </div>
                  <button type="button" onClick={() => changeSection('orders')}>
                    {t('customerUi.userPanel.viewOrders')} <FiChevronLeft />
                  </button>
                </section>

                <div className="user-panel-stats-grid">
                  <article className="user-panel-stat-card is-blue">
                    <div className="user-panel-stat-top">
                      <span className="user-panel-stat-icon"><FiShoppingBag /></span>
                      <span className="user-panel-stat-label">{t('customerUi.userPanel.allOrders')}</span>
                    </div>
                    <strong>{dashboardLoading ? '—' : formatNumber(dashboardSummary.total)}</strong>
                    <small>{t('customerUi.userPanel.allOrdersHint')}</small>
                  </article>
                  <article className="user-panel-stat-card is-green">
                    <div className="user-panel-stat-top">
                      <span className="user-panel-stat-icon"><FiCheckCircle /></span>
                      <span className="user-panel-stat-label">{t('customerUi.userPanel.successfulPayments')}</span>
                    </div>
                    <strong>{dashboardLoading ? '—' : formatNumber(dashboardSummary.paid)}</strong>
                    <small>{t('customerUi.userPanel.successfulPaymentsHint')}</small>
                  </article>
                  <article className="user-panel-stat-card is-cyan">
                    <div className="user-panel-stat-top">
                      <span className="user-panel-stat-icon"><FiDownload /></span>
                      <span className="user-panel-stat-label">{t('customerUi.userPanel.downloadable')}</span>
                    </div>
                    <strong>{dashboardLoading ? '—' : formatNumber(dashboardSummary.downloadable)}</strong>
                    <small>{t('customerUi.userPanel.downloadableHint')}</small>
                  </article>
                  <article className="user-panel-stat-card is-violet">
                    <div className="user-panel-stat-top">
                      <span className="user-panel-stat-icon"><FiCreditCard /></span>
                      <span className="user-panel-stat-label">{t('customerUi.userPanel.totalPaid')}</span>
                    </div>
                    <strong className="user-panel-stat-amount">
                      {dashboardLoading
                        ? '—'
                        : dashboardSummary.spentByCurrency?.length
                          ? dashboardSummary.spentByCurrency.map((item) => `${formatAmount(item.amount)} ${item.currency}`).join(' / ')
                          : `0 ${locale.currencyLabel || t('customerUi.common.toman')}`}
                    </strong>
                    <small>{t('customerUi.userPanel.totalPaidHint')}</small>
                  </article>
                </div>

                <div className="user-panel-dashboard-grid">
                  <section className="user-panel-section-card user-panel-recent-orders">
                    <div className="user-panel-section-heading">
                      <div>
                        <span className="user-panel-section-icon"><FiClock /></span>
                        <div>
                          <h3>{t('customerUi.userPanel.recent')}</h3>
                          <p>{t('customerUi.userPanel.recentHint')}</p>
                        </div>
                      </div>
                      <nav className="user-panel-recent-actions" aria-label={t('customerUi.userPanel.viewOrders')}>
                        <button type="button" onClick={() => changeSection('orders')}>{t('customerUi.userPanel.purchases')}</button>
                        <button type="button" onClick={() => changeSection('service-orders')}>{translatedSections.find((item) => item.id === 'service-orders')?.label}</button>
                      </nav>
                    </div>
                    {dashboardLoading ? (
                      <div className="user-panel-loading"><span /> {t('customerUi.userPanel.loadingOrders')}</div>
                    ) : recentOrders.length === 0 ? (
                      <div className="user-panel-empty user-panel-empty--compact">
                        <span><FiShoppingCart /></span>
                        <div><strong>{t('customerUi.userPanel.noOrders')}</strong><p>{t('customerUi.userPanel.noOrdersHint')}</p></div>
                      </div>
                    ) : (
                      <div className="user-panel-recent-list">
                        {recentOrders.map(({ type, order }) => {
                          const isService = type === 'service';
                          const serviceStatus = isService ? statusInfo(order.status) : null;
                          const isPaid = isService ? isPaidServiceOrder(order) : isPaidProductOrder(order);
                          const amount = isService
                            ? order.paymentSchedule?.totalAmount || order.quote?.amount
                            : order.amount;
                          return (
                          <button
                            type="button"
                            className="user-panel-recent-item"
                            key={`${type}-${order._id || order.id}`}
                            onClick={() => (
                              isService
                                ? navigate(localePath(userServiceOrderDetailPath(order._id || order.id)))
                                : changeSection('orders')
                            )}
                          >
                            <span className={`user-panel-recent-icon ${isService ? 'is-service' : ''}`}>{isService ? <FiBriefcase /> : <FiPackage />}</span>
                            <div>
                              <strong>{isService ? (order.brief?.projectTitle || order.serviceSnapshot?.title || t('customerUi.userPanel.service')) : (order.productSnapshot?.name || order.product?.name || t('customerUi.userPanel.product'))}</strong>
                              <small>{isService ? t('customerUi.userPanel.services') : t('customerUi.userPanel.product')} · #{shortOrderId(order)} · {formatDate(order.createdAt)}</small>
                            </div>
                            <div className="user-panel-recent-price">
                              <strong>{amount ? `${formatAmount(amount)} ${order.currencyLabel || order.paymentSchedule?.currency || locale.currencyLabel}` : t('customerUi.userPanel.awaitingPrice')}</strong>
                              <span className={isPaid ? 'is-paid' : 'is-unpaid'}>
                                {isService
                                  ? order.paymentStatus === 'not_required'
                                    ? serviceStatus.label
                                    : paymentStatusLabel(order.paymentStatus)
                                  : (PRODUCT_ORDER_LABELS[locale.code]?.[order.paymentStatus] || order.paymentStatus || t('customerUi.userPanel.unpaid'))}
                              </span>
                            </div>
                          </button>
                        )})}
                      </div>
                    )}
                  </section>

                  {!serviceOrdersLoading && supportSubscriptions.length > 0 && (
                    <div className="user-panel-dashboard-side">
                      <section className="user-panel-support-section" aria-labelledby="user-support-title">
                        <div className="user-panel-support-heading">
                          <div>
                            <span><FiShield /></span>
                            <div>
                              <h3 id="user-support-title">{t('customerUi.userPanel.support.title')}</h3>
                              <p>{t('customerUi.userPanel.support.hint')}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => changeSection('service-orders')}
                            aria-label={t('customerUi.userPanel.support.viewOrders')}
                          >
                            <FiChevronLeft />
                          </button>
                        </div>
                        <div className="user-panel-support-grid">
                          {supportSubscriptions.map((subscription) => {
                            const order = subscription.order;
                            const statusKey = SUPPORT_STATUS_TONES[subscription.status] ? subscription.status : 'inactive';
                            const statusTone = SUPPORT_STATUS_TONES[statusKey];
                            const statusLabel = t(`customerUi.userPanel.support.statuses.${statusKey}`);
                            const isExpired = ['expired', 'cancelled'].includes(subscription.status);
                            const remainingAmount = Number(order.paymentSchedule?.remainingAmount) || 0;
                            const durationMonths = Number(subscription.durationMonths || order.planSnapshot?.durationMonths || 0);
                            return (
                              <article
                                key={order._id || order.id}
                                className={`user-panel-support-card ${statusTone}`}
                                style={{ '--support-remaining': `${subscription.remainingPercent}%` }}
                              >
                                <header>
                                  <div>
                                    <span className="user-panel-support-card-icon"><FiShield /></span>
                                    <div>
                                      <small>{t('customerUi.userPanel.support.plan')}</small>
                                      <h4>{subscription.planName || order.planSnapshot?.name || t('customerUi.userPanel.support.defaultPlan')}</h4>
                                    </div>
                                  </div>
                                  <span className={`user-panel-support-status ${statusTone}`}>{statusLabel}</span>
                                </header>
                                <div className="user-panel-support-body">
                                  <div className="user-panel-support-countdown">
                                    <span>
                                      <strong>{formatNumber(subscription.daysRemaining)}</strong>
                                      <small>{t('customerUi.userPanel.support.daysRemaining')}</small>
                                    </span>
                                  </div>
                                  <dl>
                                    <div><dt>{t('customerUi.userPanel.support.website')}</dt><dd dir="ltr">{subscription.website || t('customerUi.userPanel.support.notRegistered')}</dd></div>
                                    <div><dt>{t('customerUi.userPanel.support.duration')}</dt><dd>{t('customerUi.userPanel.support.months', { count: durationMonths })}</dd></div>
                                    <div><dt>{t('customerUi.userPanel.support.start')}</dt><dd>{formatDate(subscription.startsAt)}</dd></div>
                                    <div><dt>{t('customerUi.userPanel.support.end')}</dt><dd>{formatDate(subscription.endsAt)}</dd></div>
                                  </dl>
                                </div>
                                <div className="user-panel-support-progress">
                                  <div>
                                    <span>
                                      {isExpired
                                        ? t('customerUi.userPanel.support.expired')
                                        : t('customerUi.userPanel.support.remaining', { percent: formatNumber(subscription.remainingPercent) })}
                                    </span>
                                    {!isExpired && (
                                      <strong>
                                        {t('customerUi.userPanel.support.daysOfTotal', {
                                          remaining: formatNumber(subscription.daysRemaining),
                                          total: formatNumber(subscription.totalDays),
                                        })}
                                      </strong>
                                    )}
                                  </div>
                                  <span><i /></span>
                                </div>
                                {remainingAmount > 0 && !isExpired && (
                                  <div className="user-panel-support-payment-note">
                                    <FiCreditCard />
                                    <span>
                                      <small>{t('customerUi.userPanel.support.balance')}</small>
                                      <strong>{formatAmount(remainingAmount)} {t('customerUi.common.toman')}</strong>
                                    </span>
                                  </div>
                                )}
                                <footer>
                                  <button
                                    type="button"
                                    onClick={() => (
                                      isExpired
                                        ? navigate(localePath('/services/website-support#pricing-plans'))
                                        : navigate(localePath(userServiceOrderDetailPath(order._id || order.id)))
                                    )}
                                  >
                                    {isExpired
                                      ? t('customerUi.userPanel.support.renew')
                                      : remainingAmount > 0
                                        ? t('customerUi.userPanel.support.payBalance')
                                        : t('customerUi.userPanel.support.details')}
                                    <FiChevronLeft />
                                  </button>
                                  <small>#{shortOrderId(order)}</small>
                                </footer>
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'orders' && (
              <div key="orders" className="user-panel-content user-panel-orders-content">
                <section className="user-panel-section-card">
                  <div className="user-panel-section-heading">
                    <div>
                      <span className="user-panel-section-icon"><FiShoppingBag /></span>
                      <div>
                        <h3>{t('customerUi.userPanel.ordersList')}</h3>
                        <p>{t('customerUi.userPanel.ordersCount', { count: formatNumber(orderSummary.total) })}</p>
                      </div>
                    </div>
                    <span className="user-panel-orders-summary">
                      <FiDownload /> {t('customerUi.userPanel.activeDownloads', { count: formatNumber(orderSummary.downloadable) })}
                    </span>
                  </div>
                  {(downloadError || paymentError) && (
                    <div className="user-panel-download-error">
                      <FiInfo /> {downloadError || paymentError}
                    </div>
                  )}
                  {ordersLoading ? (
                    <div className="user-panel-loading user-panel-loading--large"><span /> {t('customerUi.userPanel.loadingOrders')}</div>
                  ) : myOrders.length === 0 ? (
                    <div className="user-panel-empty">
                      <span><FiShoppingCart /></span>
                      <div>
                        <strong>{t('customerUi.userPanel.noOrderYet')}</strong>
                        <p>{t('customerUi.userPanel.noOrderYetHint')}</p>
                        <button type="button" onClick={() => navigate(localePath('/products'))}>{t('customerUi.userPanel.viewProducts')} <FiChevronLeft /></button>
                      </div>
                    </div>
                  ) : (
                    <ul className="user-panel-orders-list">{myOrders.map(renderOrderCard)}</ul>
                  )}
                </section>
              </div>
            )}

            {activeSection === 'service-orders' && (
              <div key="service-orders" className="user-panel-content user-panel-service-orders-content">
                <UserServiceOrders
                  orderId={userRoute.serviceOrderId}
                  onOpenOrder={(orderId) => navigate(localePath(userServiceOrderDetailPath(orderId)))}
                  onCloseOrder={() => navigate(localePath(userServiceOrdersPath()), { replace: true })}
                  onCountChange={setServiceOrderCount}
                />
              </div>
            )}

            {activeSection === 'payments' && (
              <div key="payments" className="user-panel-content">
                <UserPayments
                  onOpenServiceOrder={(orderId) => navigate(localePath(userServiceOrderDetailPath(orderId)))}
                  onOpenProductOrders={() => changeSection('orders')}
                />
              </div>
            )}

            {activeSection === 'settings' && (
              <div key="settings" className="user-panel-content">
                <div className="user-panel-profile-layout">
                  <aside className="user-panel-profile-card">
                    <span className="user-panel-profile-card-label">{t('customerUi.userPanel.profile')}</span>
                    <div className="user-panel-profile-avatar">
                      {avatarSrc ? (
                        <img src={avatarSrc} alt={displayName} onError={() => setAvatarError(true)} />
                      ) : (
                        <span>{displayName.charAt(0)}</span>
                      )}
                    </div>
                    <h2>{displayName}</h2>
                    <p>{user?.email || '—'}</p>
                    <div className="user-panel-profile-progress">
                      <div><span>{t('customerUi.userPanel.profileCompletion')}</span><strong>{formatNumber(profileCompletion)}٪</strong></div>
                      <span><i style={{ width: `${profileCompletion}%` }} /></span>
                    </div>
                    <div className="user-panel-profile-meta">
                      <span><FiPhone /> {profile.phone || t('customerUi.userPanel.phoneMissing')}</span>
                      <span><FiMapPin /> {[profile.city, profile.province].filter(Boolean).join('، ') || t('customerUi.userPanel.locationMissing')}</span>
                      <span><FiGlobe /> {profile.website || t('customerUi.userPanel.websiteMissing')}</span>
                    </div>
                  </aside>

                  <section className="user-panel-form-card">
                    <div className="user-panel-form-heading">
                      <span><FiEdit3 /></span>
                      <div><h2>{t('customerUi.userPanel.personalInfo')}</h2><p>{t('customerUi.userPanel.personalInfoHint')}</p></div>
                    </div>
                    <div className="user-panel-form-grid">
                      <div className="user-panel-field">
                        <label htmlFor="firstName">{t('customerUi.userPanel.firstName')}</label>
                        <input id="firstName" type="text" value={profile.firstName} onChange={(event) => handleProfileChange('firstName', event.target.value)} placeholder={t('customerUi.userPanel.firstNamePlaceholder')} />
                      </div>
                      <div className="user-panel-field">
                        <label htmlFor="lastName">{t('customerUi.userPanel.lastName')}</label>
                        <input id="lastName" type="text" value={profile.lastName} onChange={(event) => handleProfileChange('lastName', event.target.value)} placeholder={t('customerUi.userPanel.lastName')} />
                      </div>
                      <div className="user-panel-field user-panel-field--readonly">
                        <label>{t('customerUi.userPanel.registeredEmail')}</label>
                        <span>{user?.email || '—'}</span>
                      </div>
                      <div className="user-panel-field">
                        <label htmlFor="phone">{t('customerUi.userPanel.phone')}</label>
                        <input id="phone" type="tel" value={profile.phone} onChange={(event) => handleProfileChange('phone', event.target.value)} placeholder="۰۹۱۲۳۴۵۶۷۸۹" />
                      </div>
                      <div className="user-panel-field">
                        <label htmlFor="city">{t('customerUi.userPanel.city')}</label>
                        <input id="city" type="text" value={profile.city} onChange={(event) => handleProfileChange('city', event.target.value)} placeholder={t('customerUi.userPanel.city')} />
                      </div>
                      <div className="user-panel-field">
                        <label htmlFor="province">{t('customerUi.userPanel.province')}</label>
                        <input id="province" type="text" value={profile.province} onChange={(event) => handleProfileChange('province', event.target.value)} placeholder={t('customerUi.userPanel.province')} />
                      </div>
                      <div className="user-panel-field user-panel-field--full">
                        <label htmlFor="address">{t('customerUi.userPanel.address')}</label>
                        <textarea id="address" value={profile.address} onChange={(event) => handleProfileChange('address', event.target.value)} placeholder={t('customerUi.userPanel.addressPlaceholder')} rows={3} />
                      </div>
                      <div className="user-panel-field user-panel-field--full">
                        <label htmlFor="website">{t('customerUi.userPanel.website')}</label>
                        <input id="website" type="url" value={profile.website} onChange={(event) => handleProfileChange('website', event.target.value)} placeholder="https://example.com" dir="ltr" />
                      </div>
                      <div className="user-panel-field user-panel-field--full">
                        <label htmlFor="bio">{t('customerUi.userPanel.bio')}</label>
                        <textarea id="bio" value={profile.bio} onChange={(event) => handleProfileChange('bio', event.target.value)} placeholder={t('customerUi.userPanel.bioPlaceholder')} rows={4} />
                      </div>
                      <div className="user-panel-field user-panel-field--full">
                        <label htmlFor="avatarFile">{t('customerUi.userPanel.avatar')}</label>
                        <label className="user-panel-avatar-uploader" htmlFor="avatarFile">
                          <span className="user-panel-avatar-uploader-preview">
                            {avatarSrc ? <img src={avatarSrc} alt="" /> : <FiUser />}
                          </span>
                          <span>
                            <strong><FiUploadCloud /> {avatarFile ? t('customerUi.userPanel.avatarSelected') : t('customerUi.userPanel.chooseAvatar')}</strong>
                            <small>{avatarFile ? avatarFile.name : t('customerUi.userPanel.avatarHint')}</small>
                          </span>
                          <input id="avatarFile" type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" onChange={handleAvatarFile} />
                        </label>
                        {avatarUploadError && <small className="user-panel-avatar-upload-error">{avatarUploadError}</small>}
                      </div>
                    </div>
                    {profileSaveStatus === 'success' && (
                      <p className="user-panel-form-message user-panel-form-message--success"><FiCheck /> {t('customerUi.userPanel.saved')}</p>
                    )}
                    {profileSaveStatus === 'error' && (
                      <p className="user-panel-form-message user-panel-form-message--error"><FiInfo /> {avatarUploadError || t('customerUi.userPanel.saveFailed')}</p>
                    )}
                    <div className="user-panel-form-actions">
                      <button type="button" className="user-panel-btn-save" onClick={saveProfile} disabled={profileSaving}><FiSave /> {profileSaving ? t('customerUi.userPanel.saving') : t('customerUi.userPanel.save')}</button>
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeSection === 'messages' && (
              <div key="messages" className="user-panel-content">
                <section className="user-panel-section-card">
                  <div className="user-panel-section-heading user-panel-section-heading--simple">
                    <div>
                      <span className="user-panel-section-icon"><FiMail /></span>
                      <div><h3>{t('customerUi.userPanel.messagesTitle')}</h3><p>{t('customerUi.userPanel.messagesHint')}</p></div>
                    </div>
                  </div>
                  <div className="user-panel-empty user-panel-empty--messages">
                    <span><FiMail /></span>
                    <div><strong>{t('customerUi.userPanel.inboxEmpty')}</strong><p>{t('customerUi.userPanel.inboxEmptyHint')}</p></div>
                  </div>
                </section>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserPanel;
